import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import axiosClient from '../../libs/axiosClient';
import styles from './MyReservation.module.scss';
import HeaderMain from '../../components/HeaderMain/HeaderMain';
import HeaderMobile from '../../components/HeaderMobile/HeaderMobile';
import Footer from '../../components/Footer/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import notice from '../../assets/hospitalReservation/notice.png';
import detailStar from '../../assets/hospitalReservation/detailStar.png';
import mockProfile from "../../assets/mockProfile.png";
import emptyIcon from "../../assets/empty.png"

interface Reservation {
  id: number;
  hospital_id: number;
  reservation_at: string;
  address_english: string;
  profile_image: string[];
  product_title: string;
  hospital_name: string;
  nearest_subway_station: string | null;
  price: number;
  is_vat_include: boolean;
  medical_status: string;
  consult_status: string;
}

function MyReservation() {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<number, { avg_score: number; total_review_quantity: number }>>({});
  const [isMobile, setIsMobile] = useState(false);
  const [selectedHospitalId, setSelectedHospitalId] = useState<number | null>(null);
  const { t, i18n } = useTranslation();
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  
  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const response = await axiosClient.get(
          '/api/v1/reservation',
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        setReservations(response.data || []);
        
        const ratingsData: Record<number, { avg_score: number; total_review_quantity: number }> = {};
        
        // 최초 API 응답에 포함시켜주실 수 있는지 문의해야 함
        // for (const reservation of response.data) {
        //   const ratingResponse = await axiosClient.get(
        //     `https://meerkat-peaceful-polliwog.ngrok-free.app/api/v1/review/${reservation.hospital_id}/stats`
        //   );
        //   ratingsData[reservation.hospital_id] = {
        //     avg_score: ratingResponse.data.avg_score,
        //     total_review_quantity: ratingResponse.data.total_review_quantity,
        //   };
        // }
        setRatings(ratingsData);
      } catch (error: any) {
        setError(
          error.response?.data?.message || 'Failed to fetch reservation data'
        );
      }
    };

    fetchReservations();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';

    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [i18n]);
  
  function formatReservationDate(dateString: string | null): string {
    if (!dateString) {
      return i18n.t('myReservation.reservationCard.reservationNot');
    }
  
    const date = new Date(dateString);
    return format(date, "MMM d. yyyy 'PM' h:mm");
  }
  
  const openChatModal = (hospitalId: number, reservation: Reservation) => {
    setSelectedHospitalId(hospitalId);
    setSelectedReservation(reservation);
  };

  const closeChatModal = () => {
    setSelectedHospitalId(null);
  };
  
  return (
    <>
      {isMobile ? (
        <HeaderMobile />
      ) : (
      <HeaderMain 
        selectedTab="myReservation"
        onTabChange={() => {}}
      />  
      )}
      <div className={styles.page}>
        <aside className={styles.sidebar}>
          <h2>{i18n.t('myReservation.header')}</h2>
          <img src={notice} alt="Notice Icon" />
          <p>{i18n.t('myReservation.notice.reschedule')}</p>
          <p>{i18n.t('myReservation.notice.cancel')}</p>
          <p>{i18n.t('myReservation.notice.contact')}</p>
          <p>{i18n.t('myReservation.notice.throughChat')}</p>
        </aside>
        <main className={styles.content}>
          {error ? (
            <p className={styles.error}>{error}</p>
          ) : reservations.length === 0 ? (
            <div className={styles.noReservationsContainer}>
              <img src={emptyIcon} alt="No Reservations" className={styles.emptyIcon} />
              <p className={styles.noReservations}>No Chat Histories.</p>
            </div>
          ) : (
            <div className={styles.reservationList}>
              {Array.isArray(reservations) && reservations.length > 0 ? (
                reservations.map((reservation) => {
                  const rating = ratings[reservation.hospital_id];
                  return (
                    <div key={reservation.id} className={styles.reservationCard}>
                      <div className={styles.cardHeader}>
                        <div className={styles.dateTime}>
                          {formatReservationDate(reservation.reservation_at)}
                          <div className={styles.container}>
                          <span
                            className={
                              reservation.consult_status === 'complete'
                                ? styles.confirmed
                                : styles.pending
                            }
                            
                          >
                            {reservation.consult_status === 'complete'
                              ? 'Confirmed'
                              : "In Progress"}
                          </span>

                            <span className={styles.daysLeft} style={{
                              display: reservation.consult_status === 'complete' ? 'flex' : 'none',
                            }}>
                              {`D-${Math.max(
                                0,
                                Math.ceil(
                                  (new Date(reservation.reservation_at).getTime() -
                                    new Date().getTime()) /
                                    (1000 * 60 * 60 * 24)
                                )
                              )}`}
                            </span>
                          </div>
                        </div>
                        <div className={styles.address}>
                          {reservation.address_english}
                        </div>
                      </div>
                      <div className={styles.divider}></div>
                      <div className={styles.cardContent}>
                        <div className={styles.image}>
                          {reservation.profile_image?.length ? (
                            <img
                              src={reservation.profile_image[0]}
                              alt="Clinic Thumbnail"
                            />
                          ) : (
                            <img
                              src={mockProfile}
                              alt="Clinic Thumbnail"
                            />
                          )}
                        </div>
                        <div className={styles.info}>
                          <h3>
                            {reservation.product_title 
                              ? reservation.product_title 
                              : i18n.t('myReservation.reservationCard.productNot')}
                          </h3>
                          {/* <div className={styles.ratingSection}>
                            <img src={detailStar} alt="Rating" className={styles.icon} />
                            {rating && (
                              <span className={styles.ratingText}>
                                {rating.avg_score.toFixed(1)} (
                                {rating.total_review_quantity})
                              </span>
                            )}
                          </div> */}
                          <div className={styles.priceContainer}>
                          <div className={styles.priceContainer}>
                            {reservation.price > 0 && (
                              <span className={styles.price}>
                                ${reservation.price.toFixed(2)}
                              </span>
                            )}
                          </div>
                            {/* {!reservation.is_vat_include && (
                              <span className={styles.vatIncluded}>
                                VAT unincluded
                              </span>
                            )} */}
                          </div>
                          <div className={styles.subInfo}>
                            <p>
                              {reservation.hospital_name}
                              {reservation.nearest_subway_station &&
                                ` | ${reservation.nearest_subway_station}`}
                            </p>
                          </div>
                          <a
                            className={styles.chatLink}
                            onClick={() => openChatModal(reservation.hospital_id, reservation)}
                          >
                            Go to Chat
                          </a>

                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles.noReservationsContainer}>
                  <img src={emptyIcon} alt="No Reservations" className={styles.emptyIcon} />
                  <p className={styles.noReservations}>No Chat Histories.</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
      {selectedHospitalId && (
        <ChatModal 
          hospitalId={selectedHospitalId.toString()}
          hospitalName={selectedReservation?.hospital_name || ""}
          onClose={closeChatModal}
          profileImage={selectedReservation?.profile_image?.[0] || mockProfile} 
        />
      )}
      <Footer />
    </>
  );
}

export default MyReservation;
